/* ============================================================================
   PDP DESKTOP IMAGE LIGHTBOX
   ============================================================================
   Desktop-only. Clicking a product PHOTO on the PDP (NOT the 3D CLAY viewer)
   opens that image large in a modal, with prev/next to walk the product's
   photos. Mobile already has zoom via pinch (image-zoom.js), so this feature
   never activates on narrow/touch layouts.

   WHY THE CLICK TARGET IS THE CONTAINER, NOT THE IMAGE
   style.css gives `.ProductImageInline` (the #ProductImageFullres photo)
   `pointer-events:none` so touches fall through to the 3D viewer. A click on a
   photo therefore resolves to the #PRODUCT_IMAGE CONTAINER. When the 3D viewer
   is the active tile, #viewer-container-wrapper sits on top capturing its own
   clicks and #PRODUCT_PAGE loses the `decoyClayHidden` class — the gate below
   keys off exactly that, so a viewer click never opens the lightbox.

   GALLERY SOURCE OF TRUTH
   #PRODUCT_OTHER_IMAGES holds one <img> per product photo (the 3D tile is not
   in it), rebuilt by BUILD_PRODUCT on every product. We read it fresh on each
   open, so the gallery always describes the current product. The start photo is
   taken from the selected thumbnail's real_src (authoritative), falling back to
   the live #ProductImageFullres src, then 0.

   ISOLATION
   No calls into main.js / BUILD_PRODUCT / SelectImage. Navigation is
   independent: prev/next only swap the modal image; they never mutate the PDP,
   the thumbnail selection, or the viewer state. The module only reads stable
   GLASS ids/classes and owns its own injected DOM.
   ========================================================================= */
(function () {
    'use strict';

    var DESKTOP_MQ = '(orientation: landscape) and (min-width: 1000px)';
    var mq = (window.matchMedia && window.matchMedia(DESKTOP_MQ)) || null;

    var overlay = null, stageEl = null, imgEl = null;
    var prevBtn = null, nextBtn = null, closeBtn = null, liveEl = null;
    var gallery = [];
    var index = 0;
    var isOpen = false;
    var lastFocus = null;
    var prevOverflow = '';

    function isDesktop() {
        return mq ? mq.matches : window.matchMedia(DESKTOP_MQ).matches;
    }

    /* All of these must hold for the photo to be a lightbox trigger. `productOpen`
       is required in addition to `decoyClayHidden` because the latter can linger
       on #PRODUCT_PAGE after the PDP closes; the cart/reviews checks keep us from
       minting a second modal on top of those overlays. */
    function eligible() {
        if (!isDesktop()) return false;
        var body = document.body;
        if (!body || !body.classList.contains('productOpen')) return false;
        if (body.classList.contains('CartOpen') || body.classList.contains('ReviewsOpen')) return false;
        var page = document.getElementById('PRODUCT_PAGE');
        return !!(page && page.classList.contains('decoyClayHidden'));
    }

    function canonical(u) {
        try { return new URL(u, location.href).href; } catch (e) { return u; }
    }

    function readGallery() {
        var out = [];
        var host = document.getElementById('PRODUCT_OTHER_IMAGES');
        if (!host) return out;
        var imgs = host.querySelectorAll('img');
        for (var i = 0; i < imgs.length; i++) {
            var s = imgs[i].getAttribute('src') || imgs[i].src;
            if (s) out.push(canonical(s));
        }
        return out;
    }

    function startIndex() {
        var target = null;
        var sel = document.querySelector('#PRODUCT_IMAGES .ImageSelected img[real_src]');
        if (sel) {
            var rs = sel.getAttribute('real_src');
            if (rs && rs !== '3dviewer') target = canonical(rs);
        }
        if (!target) {
            var full = document.getElementById('ProductImageFullres');
            if (full && full.getAttribute('src')) target = canonical(full.getAttribute('src'));
        }
        if (target) {
            for (var i = 0; i < gallery.length; i++) {
                if (gallery[i] === target) return i;
            }
        }
        return 0;
    }

    function mkBtn(cls, label, icon) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = cls;
        b.setAttribute('aria-label', label);
        b.innerHTML = '<svg class="decoy-icon" aria-hidden="true" focusable="false">' +
                      '<use href="#i-' + icon + '"></use></svg>';
        return b;
    }

    function ensureDOM() {
        if (overlay) return;
        overlay = document.createElement('div');
        overlay.id = 'DECOY_PDP_LIGHTBOX';
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-label', 'Product image gallery');
        overlay.hidden = true;

        stageEl = document.createElement('div');
        stageEl.className = 'dpl-stage';
        imgEl = document.createElement('img');
        imgEl.className = 'dpl-img';
        imgEl.alt = '';
        imgEl.setAttribute('draggable', 'false');
        stageEl.appendChild(imgEl);

        closeBtn = mkBtn('dpl-close', 'Close image gallery', 'close');
        prevBtn = mkBtn('dpl-nav dpl-prev', 'Previous image', 'arrow_back');
        nextBtn = mkBtn('dpl-nav dpl-next', 'Next image', 'arrow_forward');

        liveEl = document.createElement('div');
        liveEl.className = 'dpl-live';
        liveEl.setAttribute('aria-live', 'polite');

        overlay.appendChild(stageEl);
        overlay.appendChild(closeBtn);
        overlay.appendChild(prevBtn);
        overlay.appendChild(nextBtn);
        overlay.appendChild(liveEl);
        document.body.appendChild(overlay);

        /* Backdrop (overlay itself, or the pointer-transparent stage) closes;
           the image and buttons handle their own clicks. */
        overlay.addEventListener('click', function (e) {
            if (e.target === overlay || e.target === stageEl) close();
        });
        closeBtn.addEventListener('click', close);
        prevBtn.addEventListener('click', function () { step(-1); });
        nextBtn.addEventListener('click', function () { step(1); });
    }

    function preloadNeighbors() {
        if (gallery.length < 2) return;
        [index - 1, index + 1].forEach(function (i) {
            var j = ((i % gallery.length) + gallery.length) % gallery.length;
            var im = new Image();
            im.src = gallery[j];
        });
    }

    function render() {
        if (!gallery.length) return;
        imgEl.src = gallery[index];
        var multi = gallery.length > 1;
        prevBtn.style.display = multi ? '' : 'none';
        nextBtn.style.display = multi ? '' : 'none';
        liveEl.textContent = multi ? ('Image ' + (index + 1) + ' of ' + gallery.length) : '';
        preloadNeighbors();
    }

    function step(d) {
        if (gallery.length < 2) return;
        index = ((index + d) % gallery.length + gallery.length) % gallery.length;
        render();
    }

    function open() {
        if (isOpen) return;
        gallery = readGallery();
        if (!gallery.length) return;
        ensureDOM();
        index = startIndex();
        lastFocus = document.activeElement;
        prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        overlay.hidden = false;
        isOpen = true;
        render();
        document.addEventListener('keydown', onKey, true);
        try { closeBtn.focus(); } catch (e) {}
    }

    function close() {
        if (!isOpen) return;
        isOpen = false;
        overlay.hidden = true;
        imgEl.removeAttribute('src');
        document.removeEventListener('keydown', onKey, true);
        document.body.style.overflow = prevOverflow;
        if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
        lastFocus = null;
    }

    function visibleButtons() {
        return [closeBtn, prevBtn, nextBtn].filter(function (b) {
            return b && b.style.display !== 'none';
        });
    }

    function onKey(e) {
        if (!isOpen) return;
        if (e.key === 'Escape') { e.preventDefault(); close(); return; }
        if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); return; }
        if (e.key === 'ArrowRight') { e.preventDefault(); step(1); return; }
        if (e.key === 'Tab') {
            var focusables = visibleButtons();
            if (!focusables.length) { e.preventDefault(); return; }
            var first = focusables[0];
            var last = focusables[focusables.length - 1];
            var active = document.activeElement;
            if (e.shiftKey) {
                if (active === first || !overlay.contains(active)) { e.preventDefault(); last.focus(); }
            } else {
                if (active === last || !overlay.contains(active)) { e.preventDefault(); first.focus(); }
            }
        }
    }

    /* --- trigger ---------------------------------------------------------- */

    function onContainerClick(e) {
        if (isOpen) return;
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        if (e.defaultPrevented) return;
        if (!eligible()) return;
        /* Never hijack a real control (the viewer disclaimer button etc.). The
           viewer wrapper is display:none while a photo shows, but guard anyway. */
        var t = e.target;
        if (t && t.closest && t.closest('#viewer-container-wrapper, button, a, [onclick]')) return;
        open();
    }

    function onContainerKey(e) {
        if (isOpen) return;
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        if (!eligible()) return;
        e.preventDefault();
        open();
    }

    /* Keep the #PRODUCT_IMAGE affordance in sync with eligibility, and tear the
       modal down whenever the state that justified it goes away. */
    function syncState() {
        var el = document.getElementById('PRODUCT_IMAGE');
        var ok = eligible();
        if (el) {
            if (ok) {
                el.setAttribute('role', 'button');
                el.setAttribute('tabindex', '0');
                el.setAttribute('aria-label', 'Open product image gallery');
            } else {
                el.removeAttribute('role');
                el.removeAttribute('tabindex');
                el.removeAttribute('aria-label');
            }
        }
        if (!ok && isOpen) close();
    }

    function init() {
        var container = document.getElementById('PRODUCT_IMAGE');
        if (container) {
            container.addEventListener('click', onContainerClick, false);
            container.addEventListener('keydown', onContainerKey, false);
        }
        if (typeof MutationObserver === 'function') {
            var page = document.getElementById('PRODUCT_PAGE');
            if (page) {
                new MutationObserver(syncState).observe(page, {
                    attributes: true, attributeFilter: ['class']
                });
            }
            new MutationObserver(syncState).observe(document.body, {
                attributes: true, attributeFilter: ['class']
            });
            /* Product-to-product navigation replaces these children in place;
               a modal describing the old product must not survive it. */
            var other = document.getElementById('PRODUCT_OTHER_IMAGES');
            if (other) {
                new MutationObserver(function () { if (isOpen) close(); })
                    .observe(other, { childList: true });
            }
        }
        if (mq) {
            var onMQ = function () { if (!isDesktop() && isOpen) close(); syncState(); };
            if (mq.addEventListener) mq.addEventListener('change', onMQ);
            else if (mq.addListener) mq.addListener(onMQ);
        }
        syncState();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
